import { useCallback, useState } from "react";

import type { TimelineDateBasis } from "@/stores/documents/repository";
import { useDocumentStore } from "@/hooks/documents/useDocumentStore";

export const TIMELINE_DATE_BASIS_STORAGE_KEY = "documents-timelineDateBasis";

/** The viewer's stored basis, or `file` when absent, invalid or unreadable. */
function loadStoredBasis(): TimelineDateBasis {
  try {
    return localStorage.getItem(TIMELINE_DATE_BASIS_STORAGE_KEY) === "upload" ? "upload" : "file";
  } catch {
    return "file";
  }
}

function storeBasis(basis: TimelineDateBasis): void {
  try {
    localStorage.setItem(TIMELINE_DATE_BASIS_STORAGE_KEY, basis);
  } catch {
    // A per-viewer convenience: an unwritable store only forgets the choice.
  }
}

/**
 * The Timeline's "Date: Upload / Creation" choice — a per-viewer preference.
 *
 * `basis` is what the Timeline actually uses. It is `file` while the database
 * cannot place rows by upload date (`uploadUnavailable`, set by the store when
 * an upload-basis read is refused), without overwriting the stored choice — so
 * once the migration is live the viewer's preference comes back on its own.
 * Read inside an `observer` component so that flag is tracked.
 */
export function useTimelineDateBasis(): {
  basis: TimelineDateBasis;
  setBasis: (basis: TimelineDateBasis) => void;
  uploadUnavailable: boolean;
} {
  const documentStore = useDocumentStore();
  const [preferred, setPreferred] = useState<TimelineDateBasis>(loadStoredBasis);
  const uploadUnavailable = documentStore.timelineDateBasisUnavailable;

  const setBasis = useCallback((next: TimelineDateBasis) => {
    setPreferred(next);
    storeBasis(next);
  }, []);

  return {
    basis: uploadUnavailable ? "file" : preferred,
    setBasis,
    uploadUnavailable,
  };
}
